/**
 * Homepage UI Logic
 * All UI-related logic: state management, event handlers, resale flow
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * Constants
 */
const RESALE_CONTEXT_KEY = 'resale_context';
const RESALE_CONTEXT_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Parse resale context from sessionStorage
 * @param {string} contextString - JSON string from sessionStorage
 * @returns {{context: object | null, error: Error | null}}
 */
export function parseResaleContext(contextString) {
  if (!contextString) {
    return { context: null, error: null };
  }

  try {
    const context = JSON.parse(contextString);
    return { context, error: null };
  } catch (error) {
    console.error('Error parsing resale context:', error);
    return {
      context: null,
      error: error instanceof Error ? error : new Error('Failed to parse resale context')
    };
  }
}

/**
 * Validate resale context
 * @param {object} context - Resale context object
 * @returns {{valid: boolean, reason: string}}
 */
export function validateResaleContext(context) {
  if (!context) {
    return {
      valid: false,
      reason: 'Context is null or undefined'
    };
  }

  // Check required fields
  const requiredFields = ['resale_id', 'email', 'preselected_product', 'timestamp'];
  for (const field of requiredFields) {
    if (!context[field]) {
      return {
        valid: false,
        reason: `Missing required field: ${field}`
      };
    }
  }

  // Check if context is not expired
  const age = Date.now() - context.timestamp;
  if (age > RESALE_CONTEXT_MAX_AGE) {
    return {
      valid: false,
      reason: 'Context expired (older than 24 hours)'
    };
  }

  return {
    valid: true,
    reason: null
  };
}

/**
 * Cleanup resale context from sessionStorage
 */
export function cleanupResaleContext() {
  try {
    sessionStorage.removeItem(RESALE_CONTEXT_KEY);
  } catch (error) {
    console.error('Error cleaning up resale context:', error);
  }
}

/**
 * Handle resale parameters from URL
 * @param {object} searchParams - URL search params
 * @param {function} setSearchParams - Function to set search params
 */
export function handleResaleParameters(searchParams, setSearchParams) {
  const resaleId = searchParams.get('resale_id');
  const email = searchParams.get('email');
  const preselectedProduct = searchParams.get('preselect_product');

  if (resaleId && email && preselectedProduct) {
    // Store resale context for after signup
    const context = {
      resale_id: resaleId,
      email: email,
      preselected_product: preselectedProduct,
      timestamp: Date.now()
    };

    try {
      sessionStorage.setItem(RESALE_CONTEXT_KEY, JSON.stringify(context));
      
      // Clean up URL parameters
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('resale_id');
      newSearchParams.delete('email');
      newSearchParams.delete('preselect_product');
      setSearchParams(newSearchParams);
    } catch (error) {
      console.error('Error storing resale context:', error);
    }
  }
}

/**
 * Check if should show resale checkout modal
 * @param {object} instance - MSAL instance
 * @param {object} resaleContext - Resale context object
 * @returns {boolean}
 */
export function shouldShowResaleCheckout(instance, resaleContext) {
  if (!instance || !resaleContext) {
    return false;
  }

  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) {
    return false;
  }

  const validation = validateResaleContext(resaleContext);
  return validation.valid;
}

/**
 * Initialize resale context from sessionStorage
 * @param {object} instance - MSAL instance
 * @param {function} setShowModal - Function to set modal visibility
 * @param {function} setContext - Function to set context state
 */
export function initializeResaleContext(instance, setShowModal, setContext) {
  if (!instance) {
    return;
  }

  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) {
    return;
  }

  try {
    const contextString = sessionStorage.getItem(RESALE_CONTEXT_KEY);
    if (!contextString) {
      return;
    }

    const { context, error } = parseResaleContext(contextString);
    
    if (error) {
      cleanupResaleContext();
      return;
    }

    if (context) {
      const validation = validateResaleContext(context);
      
      if (validation.valid) {
        setShowModal(true);
        setContext(context);
      } else {
        // Remove invalid/expired context
        cleanupResaleContext();
      }
    }
  } catch (error) {
    console.error('Error initializing resale context:', error);
    cleanupResaleContext();
  }
}

/**
 * Hook for managing resale flow
 * @param {object} instance - MSAL instance
 * @param {object} searchParams - URL search params
 * @param {function} setSearchParams - Function to set search params
 * @returns {{showResaleCheckout: boolean, resaleContext: object | null, handleClose: function, cleanup: function}}
 */
export function useResaleFlow(instance, searchParams, setSearchParams) {
  const [showResaleCheckout, setShowResaleCheckout] = useState(false);
  const [resaleContext, setResaleContext] = useState(null);

  // Handle resale parameters from URL
  useEffect(() => {
    handleResaleParameters(searchParams, setSearchParams);
  }, [searchParams, setSearchParams]);

  // Check for resale context after authentication
  useEffect(() => {
    initializeResaleContext(instance, setShowResaleCheckout, setResaleContext);
  }, [instance]);

  const handleClose = useCallback(() => {
    setShowResaleCheckout(false);
    setResaleContext(null);
    cleanupResaleContext();
  }, []);

  const cleanup = useCallback(() => {
    cleanupResaleContext();
  }, []);

  return {
    showResaleCheckout,
    resaleContext,
    handleClose,
    cleanup
  };
}


